import { ADMIN_PAGES } from "../shared/adminPages";
import { DOCUMENT_MODULES, type DocumentCategory } from "../shared/documentModules";
/** URL scope is a route contract, independent of the current user's permissions.
 * Authorization always happens during admission and again at the API boundary.
 * Mixed namespaces (admin/integrations) deliberately declare individual families.
 */
export type RouteScope = "project" | "global" | "public" | "transport";

/**
 * What a page is part of, as usage reports name it (`active_time_slice.feature`, read by ai_engine's
 * `app/engagement/features.py`). Every page route says its own: there is no default, so a page nobody
 * classified fails `routeContract.test.ts` instead of being filed as Home. `home` is the project
 * overview alone; `authentication`, `navigation`, `settings` and `admin` make someone active without
 * making them a feature's user.
 */
export type RouteFeature =
    | "home"
    | "marvin"
    | "viewer"
    | "drawings"
    | "specifications"
    | "documents"
    | "schedule"
    | "rfi"
    | "submittals"
    | "field"
    | "search"
    | "workbench"
    | "revision_narrative"
    | "constructability"
    | "control_tower"
    | "quality"
    | "timeline"
    | "shop_review"
    | "clash_review"
    | "file_comparison"
    | "tools"
    | "tables"
    | "team"
    | "directory"
    | "portfolio"
    | "integrations"
    | "authentication"
    | "navigation"
    | "settings"
    | "admin";

interface RouteContract {
    path: string;
    scope: RouteScope;
    /** Every page's feature; only transport paths and token pages (never measured) have none. */
    feature?: RouteFeature;
    switchTo?: string;
    /** A global page that runs without a project: a staff registry session (no project)
     *  opens it with a project-free query client instead of being asked for a project, and
     *  the shell draws no project areas on it (`useProjectChrome`). */
    projectFree?: true;
}
const project = (path: string, feature: RouteFeature, switchTo = path): RouteContract => ({
    path,
    scope: "project",
    feature,
    switchTo,
});
const global = (path: string, feature: RouteFeature): RouteContract => ({
    path,
    scope: "global",
    feature,
});
/** Project settings sections live under `/settings`; every other admin page is the admin area. */
const adminFeature = (path: string): RouteFeature =>
    path.startsWith("/settings") ? "settings" : "admin";
/**
 * Every admin page's contract (`ADMIN_PAGES`), and a project route for each moved page's old
 * address, which redirects. A longer path comes before a page it sits under, since the first
 * match wins (`/admin/flags/global` is staff's, `/admin/flags` the tenant's).
 */
const ADMIN_CONTRACTS: readonly RouteContract[] = [
    ...ADMIN_PAGES.flatMap((page): RouteContract[] =>
        page.scope === "project"
            ? [project(page.path, adminFeature(page.path), page.switchTo)]
            : page.scope === "global"
              ? [
                    {
                        ...global(page.path, adminFeature(page.path)),
                        ...(page.projectFree && { projectFree: true }),
                    },
                ]
              : [],
    ),
    ...ADMIN_PAGES.flatMap((page) =>
        page.movedFrom ? [project(page.movedFrom, adminFeature(page.path), page.path)] : [],
    ),
].sort((a, b) => b.path.length - a.path.length);

export const ROUTE_CONTRACTS: readonly RouteContract[] = [
    ...ADMIN_CONTRACTS,
    { path: "/guest", scope: "public" },
    // A product update email's stop page (signed out, keyed by the copy's token).
    { path: "/email/stop", scope: "public" },
    ...["/api", "/static", "/embedpdf", "/src", "/manifest.json", "/favicon.ico"].map(
        (path): RouteContract => ({ path, scope: "transport" }),
    ),
    ...(
        [
            ["/authenticate", "authentication"],
            ["/embed-auth", "authentication"],
            ["/embed-test", "integrations"],
            ["/embed", "integrations"],
            ["/logout", "authentication"],
            ["/projects", "navigation"],
            ["/offline", "documents"],
            ["/favorites", "documents"],
            ["/this-machine", "documents"],
            // The Connected Apps redirect is registered bare. A URL-catalog session cannot
            // use the canonical consent endpoint, so the page is never moved under a prefix.
            ["/oauth/authorize", "authentication"],
            // The retired Engagement reports' address: it redirects to Usage, which runs without a project.
            ["/admin/engagement", "admin"],
        ] as const
    ).map(([path, feature]) => global(path, feature)),
    // The portfolio names the tenants it reads, but the header and Marvin act on the tab's project.
    project("/portfolio", "portfolio"),
    ...(
        [
            ["cr", "constructability"],
            ["control-tower", "control_tower"],
            ["workflow-studio", "tools"],
            ["rn", "revision_narrative"],
            ["fc", "file_comparison"],
            ["quality", "quality"],
            ["rfi", "rfi"],
            ["search", "search"],
            // Marvin Explore keeps the workbench bucket across its rename so the series stays whole.
            ["explore", "workbench"],
            ["workbench", "workbench"],
            ["shop-review", "shop_review"],
            ["clash-review", "clash_review"],
            // Requirements check stays "tools", the bucket it has always reported under.
            ["rc", "tools"],
            ["submittal", "submittals"],
            ["submittal-register", "submittals"],
            ["submittal-registry", "submittals"],
            ["upload", "documents"],
        ] as const
    ).map(([name, feature]) => project(`/w/${name}`, feature)),
    project("/w/tools", "tools", "/"),
    project("/rfi-links", "rfi", "/rfi-links/review"),
    project("/sheets/revisions", "documents", "/admin/revisions"),
    project("/doc/specs", "viewer", "/doc/specs"),
    project("/doc", "viewer", "/doc/sheets"),
    // Its categories are split by module (`documentModuleFeature`): RFIs, submittals, sheets, field records.
    project("/documents", "documents"),
    project("/c", "marvin"),
    project("/table-edit", "tables", "/documents"),
    project("/integrations/data-status", "integrations"),
    project("/integrations", "integrations"),
    project("/settings/project", "settings"),
    project("/settings/workflows", "settings"),
    project("/settings", "settings"),
    project("/apps", "navigation"),
    ...(
        [
            ["/drawings", "drawings"],
            ["/specifications", "specifications"],
            ["/specs", "specifications"],
            ["/sheets", "drawings"],
            ["/team", "team"],
            ["/directory", "directory"],
            ["/areas", "documents"],
            ["/rfis", "rfi"],
            ["/submittals", "submittals"],
            ["/schedule", "schedule"],
            ["/timeline", "timeline"],
            // The model viewer reports as Viewer until the event registry names a models page feature.
            ["/models", "viewer"],
            ["/debug", "admin"],
            ["/connect", "integrations"],
        ] as const
    ).map(([path, feature]) => project(path, feature)),
    // Only the index routes: these must not silently classify a new admin/tool route. `/admin`
    // (the admin Overview) comes from ADMIN_CONTRACTS.
    project("/", "home"),
];

export function routeContract(path: string): RouteContract | null {
    const pathname = path.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
    const lower = pathname.toLowerCase();
    return (
        ROUTE_CONTRACTS.find(({ path: prefix }) => {
            const root = prefix.toLowerCase();
            return (
                lower === root ||
                (root !== "/" && root !== "/admin" && lower.startsWith(`${root}/`))
            );
        }) ?? null
    );
}

/**
 * The Documents area's modules each count as what they are: the RFI and submittal registers as RFIs and
 * submittals, the sheet and spec lists as drawings and specifications, the field records as field work.
 * File categories and the overview stay Documents. Keyed by module, so a new module must say.
 */
const DOCUMENT_MODULE_FEATURES: Readonly<Record<DocumentCategory | "overview", RouteFeature>> = {
    overview: "documents",
    "my-work": "documents",
    reports: "documents",
    form: "field",
    rfi: "rfi",
    submittal: "submittals",
    transmittal: "documents",
    punch: "field",
    inspection: "quality",
    inspection_round: "quality",
    ddnc: "quality",
    issue: "field",
    daily_report: "field",
    meeting: "field",
    provision: "field",
    sheets: "drawings",
    specifications: "specifications",
    photos: "field",
    contracts: "documents",
    schedule: "schedule",
    documents: "documents",
    "acc-files": "documents",
    "procore-files": "documents",
    "sharepoint-files": "documents",
    code: "documents",
    ifoa: "documents",
    primecontract: "documents",
    supplemental: "documents",
};

/** The feature of the page at `path` (a route path, `routePath`): its route's, or, inside Documents, its
 *  module's. Null only for a path no route serves (a mistyped URL) and for transport and token paths. */
export function routeFeature(path: string): RouteFeature | null {
    const contract = routeContract(path);
    if (contract?.path === "/documents") {
        const categoryPath = path
            .split(/[?#]/, 1)[0]
            .split("/")
            .slice(0, 3)
            .join("/")
            .toLowerCase();
        for (const [key, module] of Object.entries(DOCUMENT_MODULES)) {
            if (module.route === categoryPath || module.fallback?.route === categoryPath) {
                return DOCUMENT_MODULE_FEATURES[key as DocumentCategory | "overview"];
            }
        }
    }
    return contract?.feature ?? null;
}

/** Switches retain a page family, never an entity/filter from another project. */
export function projectSwitchPath(path: string): string {
    const pathname = path.split(/[?#]/, 1)[0];
    // A project's page names its project; a switch keeps the family, never the other project.
    if (/^\/admin\/revisionDash(?:\/|$)/i.test(pathname)) return "/admin/revisionDash";
    const contract = routeContract(pathname);
    if (contract?.path === "/documents") {
        const categoryPath = pathname.split("/").slice(0, 3).join("/");
        // Use the module catalogue, never carry a legacy document UUID as a category.
        return Object.values(DOCUMENT_MODULES).some((module) => module.route === categoryPath)
            ? categoryPath
            : "/documents";
    }
    return contract?.switchTo ?? "/";
}
